/**
 * Research outcome classification and status-badge display.
 *
 * A research run that ends in `failed` covers two very different situations:
 *
 *   1. The record was too sparse for the agent to build a single search query.
 *      Nothing broke — the run simply had nothing to go on, and the user fixes
 *      it by filling in a field. This is `needs_more_info`, and it renders as a
 *      calm empty state, not an error.
 *   2. A genuine failure — a search-provider outage, an AI synthesis error, a
 *      crashed run. This stays `failed` and keeps the error treatment.
 *
 * ⚠️ KNOWN GAP — there is no structured discriminator on the wire.
 * `apps/research/services/research_service.py` raises distinct exception types
 * (`InsufficientDataError`, `TavilyError`, `NoSearchResultsError`,
 * `SynthesisError`) but collapses every one of them onto the same two
 * properties: `ahoy_status = 'failed'` plus a free-text `ahoy_error_message`.
 * The `ahoy_research` schema
 * (`apps/crm_events/schemas/properties/research_properties.yaml`) has no
 * error-code property, so the only signal that reaches the browser is the
 * message string — and the classification below has to match on it.
 *
 * The correct fix is a structured `ahoy_error_code` select on `ahoy_research`,
 * written alongside `ahoy_error_message` and read here instead of the string
 * match. That is a backend schema change (yaml + service + backfill) and is
 * deliberately out of scope for this frontend change. Until it lands, this
 * module is the single place the brittle match lives — do not re-derive the
 * classification anywhere else.
 */

import type { Research, ResearchStatus } from './useResearch';

/** The two record types research runs against. */
export type ResearchRecordType = 'contact' | 'company';

/**
 * Resolve the record type an entity's research copy should speak to.
 * Defaults to `contact`, matching the backend's own default.
 */
export function researchRecordTypeFor(object?: {
  entity_type_name?: string | null;
}): ResearchRecordType {
  return object?.entity_type_name === 'ahoy_company' ? 'company' : 'contact';
}

/**
 * Distinctive, stable fragment of the backend's `InsufficientDataError`
 * message. Mirrors `apps/research/services/research_service.py` — the literal
 * there reads "Not enough information to research this contact. Please add a
 * name, company, or business email address."
 */
export const INSUFFICIENT_INFO_MESSAGE_MARKER =
  'not enough information to research';

/**
 * Whether a stored research error message describes the record being too
 * sparse to research, rather than something going wrong.
 */
export function isInsufficientInfoMessage(
  errorMessage: string | null | undefined
): boolean {
  if (!errorMessage) {
    return false;
  }
  return errorMessage.toLowerCase().includes(INSUFFICIENT_INFO_MESSAGE_MARKER);
}

/**
 * Research status as the UI presents it: the raw statuses, plus the
 * `needs_more_info` refinement carved out of `failed`.
 */
export type ResearchOutcome = ResearchStatus | 'needs_more_info';

/**
 * Classify a research item for display.
 */
export function getResearchOutcome(
  research: Pick<Research, 'status' | 'error_message'>
): ResearchOutcome {
  if (
    research.status === 'failed' &&
    isInsufficientInfoMessage(research.error_message)
  ) {
    return 'needs_more_info';
  }
  return research.status;
}

export interface ResearchStatusBadge {
  color: 'gray' | 'blue' | 'success' | 'error';
  label: string;
}

const RESEARCH_STATUS_BADGES: Record<ResearchOutcome, ResearchStatusBadge> = {
  queued: { color: 'gray', label: 'Queued' },
  in_progress: { color: 'blue', label: 'In Progress' },
  complete: { color: 'success', label: 'Complete' },
  failed: { color: 'error', label: 'Failed' },
  // Nothing failed here, so the pill must not read as an error.
  needs_more_info: { color: 'gray', label: 'Needs More Info' },
};

const UNKNOWN_STATUS_BADGE: ResearchStatusBadge = {
  color: 'gray',
  label: 'Unknown',
};

/**
 * Badge colour and label for a research outcome. Shared by the slideout
 * section, the full-page tab, and the history list so the pill can never drift
 * between surfaces.
 */
export function getResearchStatusBadge(
  outcome: ResearchOutcome
): ResearchStatusBadge {
  return RESEARCH_STATUS_BADGES[outcome] ?? UNKNOWN_STATUS_BADGE;
}
